import React from 'react';
import { Cpu, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ProcessingSection } from '../../api/insights';
import shared from './shared.module.css';
import styles from './ProcessingPipelineGroup.module.css';

interface Props {
  data: ProcessingSection;
}

const STATUS_LABELS: Record<string, string> = {
  idle: 'Not started',
  running: 'Indexing…',
  completed: 'Complete',
  failed: 'Failed',
};

const STATUS_CLASS: Record<string, string> = {
  idle: shared['badge--neutral'],
  running: shared['badge--amber'],
  completed: shared['badge--green'],
  failed: shared['badge--red'],
};

export function ProcessingPipelineGroup({ data }: Props) {
  const total = data.overlays.processed + data.overlays.pending + data.overlays.failed;
  const processedPct = total > 0 ? (data.overlays.processed / total) * 100 : 0;
  const pendingPct = total > 0 ? (data.overlays.pending / total) * 100 : 0;
  const failedPct = total > 0 ? (data.overlays.failed / total) * 100 : 0;

  return (
    <div className={shared['group-card']}>
      <div className={shared['group-header']}>
        <div className={shared['group-icon']}><Cpu size={15} /></div>
        <div>
          <div className={shared['group-title']}>Post-Upload Processing</div>
          <div className={shared['group-subtitle']}>Overlay compositing, face indexing, and ZIP builds</div>
        </div>
      </div>

      {/* Overlay compositing */}
      <div>
        <div className={styles['overlay-header']}>
          <div className={shared['section-label']} style={{ marginBottom: 0 }}>Overlay compositing</div>
          <span className={`${shared.badge} ${data.overlays.successRate >= 98 ? shared['badge--green'] : data.overlays.successRate >= 90 ? shared['badge--amber'] : shared['badge--red']}`}>
            {data.overlays.successRate}% success
          </span>
        </div>
        {/* Three-segment bar */}
        <div className={styles['segment-bar']} aria-label="Compositing status">
          <div className={styles['segment-green']} style={{ width: `${processedPct}%` }} title={`Processed: ${data.overlays.processed}`} />
          <div className={styles['segment-amber']} style={{ width: `${pendingPct}%` }} title={`Pending: ${data.overlays.pending}`} />
          <div className={styles['segment-red']} style={{ width: `${failedPct}%` }} title={`Failed: ${data.overlays.failed}`} />
        </div>
        <div className={styles['segment-legend']}>
          <span className={styles['seg-label']}><span className={styles['seg-dot-green']} />Processed {data.overlays.processed.toLocaleString()}</span>
          <span className={styles['seg-label']}><span className={styles['seg-dot-amber']} />Pending {data.overlays.pending.toLocaleString()}</span>
          <span className={styles['seg-label']}><span className={styles['seg-dot-red']} />Failed {data.overlays.failed.toLocaleString()}</span>
        </div>
      </div>

      {/* Failed albums */}
      {data.failedAlbums.length > 0 && (
        <div className={styles['failed-albums']}>
          <div className={shared['section-label']}>Albums with failures</div>
          {data.failedAlbums.map((a) => (
            <div key={a.albumId} className={styles['failed-album-row']}>
              <span className={styles['failed-album-name']}>{a.albumName}</span>
              <span className={`${shared.badge} ${shared['badge--red']}`}>{a.failedCount} failed</span>
              <Link to={`/events/${a.eventId}`} className={styles['retry-link']}>
                <RefreshCw size={11} /> Retry
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* Face indexing */}
      <div>
        <div className={shared['section-label']}>Face indexing per event</div>
        {data.faceIndexing.filter((e) => e.enabled).length === 0 ? (
          <span className={shared['sub-empty']}>No events have face recognition enabled</span>
        ) : (
          <div className={styles['face-list']}>
            {data.faceIndexing.filter((e) => e.enabled).map((e) => {
              const pct = e.totalPhotos > 0 ? Math.round((e.indexedCount / e.totalPhotos) * 100) : 0;
              return (
                <div key={e.eventId} className={styles['face-row']}>
                  <div className={styles['face-row-top']}>
                    <span className={styles['face-event-name']}>{e.eventName}</span>
                    <span className={`${shared.badge} ${STATUS_CLASS[e.status]}`}>{STATUS_LABELS[e.status]}</span>
                  </div>
                  <div className={styles['face-progress-row']}>
                    <div className={shared['progress-track']} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                      <div
                        className={`${shared['progress-fill']} ${e.status === 'failed' ? shared['progress-fill--red'] : shared['progress-fill--green']}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className={styles['face-counts']}>{e.indexedCount} / {e.totalPhotos}</span>
                  </div>
                  {e.failureCount > 0 && (
                    <span className={styles['face-failures']}>{e.failureCount} photo{e.failureCount > 1 ? 's' : ''} could not be indexed</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ZIPs */}
      <div className={styles['zip-row']}>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}>ZIP archives ready</span>
          <span className={`${shared['stat-value']} ${shared['stat-value--green']}`}>{data.zips.ready}</span>
        </div>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}>Stale ZIPs</span>
          <span className={`${shared['stat-value']} ${data.zips.stale > 0 ? shared['stat-value--amber'] : ''}`}>{data.zips.stale}</span>
          {data.zips.stale > 0 && <span className={shared['stat-sub']}>need rebuild</span>}
        </div>
      </div>
    </div>
  );
}
